import { AfterViewInit, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { SchemaApiService } from '../../core/services/schema-api.service';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
} from 'rxjs';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSortModule, Sort } from '@angular/material/sort'; // Required for matSort in HTML
import { ActivatedRoute, Router } from '@angular/router';
import { RenderFormPageComponent } from './renderentityform/render-form-page';
import { WorkflowEntityPanelComponent } from '../../feature/workflow/workflow-panel.component';
import { WorkflowActionBarComponent } from '../../feature/workflow/workflow-action-bar.component';
import { DynamicFormLayout } from '../../core/models/dynamic-form.models';
import { MetaService } from '../../core/services/meta.service';


// Type safety for onPage(e: PageEvent)

export interface FilterField {
  field: string;
  label: string;
  filterType: string | null; // ✅ CORRECT
  options?: string[];
}

declare var KTDrawer: any;

@Component({
  selector: 'app-invoice-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    RenderFormPageComponent,
    WorkflowEntityPanelComponent,
    WorkflowActionBarComponent
  ],
  templateUrl: './renderentity-list-page.component.html',
})
export class RenderEntityListPageComponent implements OnInit {
  // ------------------------
  // BASIC
  // ------------------------
  entity = '';
  metaEntity: DynamicFormLayout = {
    placement: 'drawer',
  } as DynamicFormLayout;
  recId?: string; // or: recId: string | undefined = undefined;
  moduleCode: string = '';
  // columnsConfig: ListColumnConfigDto[] = [];

  columnsConfig: any[] = [];
  displayedColumns: string[] = [];

  // ------------------------
  // DATA
  // ------------------------
  dataSource: any[] = [];
  total = 0;

  // ------------------------
  // SEARCH / FILTER / SORT
  // ------------------------
  searchValue = '';
  search$ = new Subject<string>();

  columnFilters: Record<string, string> = {};
  // advancedFilters: any = {};
  showFilter = false;
  showAdvancedFilter = false;

  filterFields: FilterField[] = [];

  // Existing Filter Object
  // advancedFilters: any= {
  //   documentno: ''
  // };
  advancedFilters: any = {};
  // 1. Control for the Autocomplete Input
  docSearchControl = new FormControl('');

  // 2. Suggestions Array
  documentSuggestions: string[] = [];

  // advancedFilters: Record<string, any> = {};

  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // ------------------------
  // PAGINATION
  // ------------------------
  pageIndex = 0;
  pageSize = 10;

  // ------------------------
  // ROW SELECTION
  // ------------------------
  selectedRow: any | null = null;

  constructor(
    private api: SchemaApiService,
    private route: ActivatedRoute,
    private _metaService: MetaService,
    private router: Router
  ) { }
  ngOnInit() {
    
    this.route.paramMap
      .subscribe(params => {

        this.moduleCode =
          params.get('module') || '';

        this.entity =
          params.get('entity') || '';

        console.log(
          'ENTITY CHANGED',
          this.moduleCode,
          this.entity
        );

        this.loadMetaEntity(this.entity);

        this.resetPage();

        this.loadConfig();

      });

    // 1. RE-ENABLE Global Search Subscriber
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(v => {

        this.searchValue = v;

        this.loadList();

      });

    // 3. Setup the search stream
    this.docSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {

          if (value && value.length >= 1) {
            return this.api.lookup(
              'api/lookups/documents',
              value
            );
          }

          return of([]);

        })
      )
      .subscribe(results => {

        this.documentSuggestions =
          results;

      });
  }

  private resetPage(): void {

    this.columnsConfig = [];

    this.displayedColumns = [];

    this.dataSource = [];

    this.filterFields = [];

    this.advancedFilters = {};

    this.selectedRow = null;

    this.pageIndex = 0;

    this.total = 0;

    this.searchValue = '';

    this.docSearchControl.setValue(
      '',
      {
        emitEvent:false
      }
    );

  }

  // 1. Helper to calculate total pages
  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize) || 1;
  }

  // 2. Handle Page Number Change
  goToPage(index: number) {
    if (index >= 0 && index < this.totalPages) {
      this.pageIndex = index;
      this.loadList(); // This triggers your API call
    }
  }

  // 3. Handle Page Size (Rows per page) Change
  changePageSize(event: Event) {
    const newSize = +(event.target as HTMLSelectElement).value;
    this.pageSize = newSize;
    this.pageIndex = 0; // Reset to first page
    this.loadList();
  }

  // 4. Helper for Range Label
  getRangeLabel(): string {
    if (this.total === 0) return '0 of 0';
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageSize, this.total);
    return `${start} – ${end} of ${this.total}`;
  }

  loadConfig() {
    this.api.getConfig(this.entity).subscribe((cfg: any[]) => {
      this.columnsConfig = cfg
        .map((c: any) => ({
          // FIX: Check both PascalCase and camelCase
          field: c.field || c.Field,
          label: c.label || c.Label,
          isVisible: c.isVisible !== undefined ? c.isVisible : c.IsVisible,
          filterType: c.filterType || c.FilterType,
          options: c.filterOptions || c.FilterOptions,
        }))
        .filter((c: any) => c.isVisible);

      // This array MUST match the keys in your data objects
      this.displayedColumns = this.columnsConfig.map((c) => c.field);

      console.log('Displayed Columns:', this.displayedColumns); // Debug: Should be ["documentno", "amount", etc]

      this.filterFields = this.columnsConfig.filter((c) => c.filterType);
      this.advancedFilters = {};
      this.filterFields.forEach((f) => {
        this.advancedFilters[f.field] = '';
      });

      this.loadList();
    });
  }
  // ------------------------
  // LOAD LIST (SERVER SIDE)
  // ------------------------
  loadList() {
    this.api
      .getList({
        entity: this.entity,
        module: this.moduleCode,
        page: this.pageIndex + 1,
        pageSize: this.pageSize,
        search: this.searchValue,
        sortField: this.sortField,
        sortDir: this.sortDir,
        filters: this.advancedFilters,
      })
      .subscribe((res) => {
        // Use a fallback to empty array to avoid "null" errors in UI
        this.dataSource = res.items || res.data || [];
        this.total = res.total || res.Total || 0;
        // this.selectedRow = null; // clear selection
      });
  }

  loadMetaEntity(code: string) {
    this._metaService.getEntityByCode(code)
      .subscribe((res : any) => {
        this.metaEntity.entity = res.entity;
        this.metaEntity.name=res.name;
        this.metaEntity.viewMode=res.viewMode;
        this.metaEntity.moduleCode=res.moduleCode;
        this.metaEntity.placement = res.placement || 'drawer';
        this.metaEntity.transType = res.transType || 'MASTER';
        //console.log('Meta Entity Loaded:', this.metaEntity);
      });
  }

  highlight(value: any): string {
    if (value == null) return '-';

    const text = value.toString();

    // Determine what we are searching for:
    // 1. Check global search (searchValue)
    // 2. Also check if the document number filter is active
    const searchTerms: string[] = [];

    if (this.searchValue) searchTerms.push(this.searchValue);
    if (this.advancedFilters.documentno)
      searchTerms.push(this.advancedFilters.documentno);

    // If nothing is being searched, return original text
    if (searchTerms.length === 0) return text;

    // Combine search terms into one regex pattern
    const pattern = searchTerms
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape special chars
      .join('|'); // Use OR logic for multiple terms

    const regex = new RegExp(`(${pattern})`, 'gi');

    return text.replace(
      regex,
      (match: string) =>
        `<span class="bg-yellow-200 px-0.5 rounded text-black font-bold">${match}</span>`,
    );
  }

  // ------------------------
  // SEARCH
  // ------------------------
  onSearchInput(value: string) {
    this.search$.next(value);
  }

  // ------------------------
  // SORT
  // ------------------------
  onSort(field: string) {
    // Same column → toggle
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    }
    // New column → start asc
    else {
      this.sortField = field;
      this.sortDir = 'asc';
    }

    console.log('SORT:', this.sortField, this.sortDir);

    this.pageIndex = 0; // reset page
    this.loadList(); // 🔥 IMPORTANT
  }

  // 🔹 PAGINATION
  onPage(e: PageEvent) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadList();
  }

  // ------------------------
  // ROW SELECTION
  // ------------------------
  selectRow(
    row:any
  ){

    if (
      this.selectedRow?.id === row.id
    ) {
      return;
    }

    this.selectedRow = {
      ...row
    };

  }

  // shwoDynamicForm: boolean = true;

  openDrawer() {
    const el = document.querySelector('#drawers_shop_cart') as HTMLElement;
    const drawer = KTDrawer.getInstance(el) || new KTDrawer(el);
    drawer.show();
  }

  closeDrawer() {
    const el = document.querySelector('#drawers_shop_cart') as HTMLElement;
    const drawer = KTDrawer.getInstance(el);
    drawer?.hide();
  }

  editRow(row: any) {
    if(this.metaEntity.placement === 'drawer') {
      this.recId = row.id;
      this.openDrawer();
    }
    else if(this.metaEntity.placement === 'page') {
      this.recId = row.id;
      this.router.navigate(
        [
          '/app/', 
          this.moduleCode, 
          this.metaEntity.transType === 'MASTER' ? 'master' : 'trans', 
          this.entity,
          this.recId
        ]);
    }
  }

  addRecord() {
    this.recId = undefined; //  create mode
    this.openDrawer();
  }
  onFormSaved() {
    this.closeDrawer();
    this.loadList(); //  refresh list
  }
  // ------------------------
  // ACTIONS
  // ------------------------
  onAction(a: any) {
    this.recId = a.recordId; // view/edit mode
    this.openDrawer();
  }
  view() {
    console.log('VIEW', this.selectedRow);
  }
  edit() {
    console.log('EDIT', this.selectedRow);
  }
  approve() {
    console.log('APPROVE', this.selectedRow);
  }
  reject() {
    console.log('REJECT', this.selectedRow);
  }

  delete() {
    if (confirm('Are you sure?')) {
      console.log('DELETE', this.selectedRow);
    }
  }

  // ------------------------
  // ADVANCED FILTER
  // ------------------------

  applyAdvancedFilters() {
    // 1. Handle the Autocomplete Field (The only one that needs manual sync)
    const autoField = this.filterFields.find(
      (f) => f.filterType === 'autocomplete',
    );

    if (autoField) {
      // We grab the raw value from the form control
      const controlVal = this.docSearchControl.value;

      // Safety check: specific logic if the value is an object (selected option) vs string (typed text)
      if (typeof controlVal === 'object' && controlVal !== null) {
        // Adjust this depending on what your object looks like, e.g., controlVal.docNo
        this.advancedFilters[autoField.field] = controlVal;
      } else {
        this.advancedFilters[autoField.field] = controlVal;
      }
    }

    // 2. Text, Number, and Date fields are ALREADY in this.advancedFilters
    // because of [(ngModel)] in the HTML. We don't need code for them here.

    // 3. Clean up empty filters (Optional but recommended)
    // This removes keys with empty strings or null values to keep the API payload clean
    const cleanFilters: any = {};
    Object.keys(this.advancedFilters).forEach((key) => {
      const value = this.advancedFilters[key];
      if (value !== null && value !== '' && value !== undefined) {
        cleanFilters[key] = value;
      }
    });

    // console.log('Final Payload:', cleanFilters);

    // 4. Trigger Load
    this.pageIndex = 0;
    // Pass cleanFilters to your load function if needed, or use this.advancedFilters
    this.loadList();
  }
}
